import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { GET_EVENT_NOTIFICATIONS, SYNC_EVENT_NOTIFICATIONS } from '@bechtel/client-gql';
import {
  Box,
  FormControl,
  Heading,
  IconSize,
  MultiSelectCheckbox,
  Select,
  Separator,
  SeparatorType,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToastAction,
  ToastType,
  theme,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  EventNotification,
  EventNotificationFilterByTerm,
  EventNotificationFilterByTermInput,
  EventNotificationFilterInput,
  EventNotificationInput,
  EventNotificationList,
  EventNotificationSortByTerm,
  EventNotificationStatus,
  EventNotificationTypeId,
  FilterOperator,
  PagingInput,
  SortOrder,
  SyncEventNotificationErrorCode,
  SyncEventNotificationsInput,
  SyncEventNotificationsPayload,
} from '@bechtel/types';
import { Trans, t } from '@lingui/macro';
import { ColDef, SortChangedEvent } from 'ag-grid-enterprise';
import { get, isEqual } from 'lodash';
import { useCallback, useDeferredValue, useEffect, useState } from 'react';
import { ProjectAdminContainerStyled, ProjectAdminHeaderStyled } from '~/styled';
import {
  Error,
  EventErrorTypes,
  EventStatusTypes,
  errorList,
  getErrorFilter,
  getStatusFilter,
  notificationTypes,
  scheduleList,
  statusList,
} from './form-data';
import { FooterButtonStyled, FooterStyled, SearchFieldComponentStyled, SubContainerStyled } from './styled';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as FilterIcon } from '@bechtel/client-assets/dist/images/filter.svg';
import { ErrorModal, ErrorRetry, ModalError } from '@bechtel/client-common';
import { useLingui } from '@lingui/react';
import { attributeColDefs } from './coldef';
import { EntityStats } from './entity-stats';
import { getError } from './errors';
import { ClientGrid } from './grid';
import { SortModelItem, SortType, mapAgGridSortToGraphQL, mapGraphQLSortToAgGrid } from './types';

const DropdownItem = ({ item }: { item: any }) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" style={{ zIndex: 3 }}>
      {item?.label ? i18n._(item.label) : ''}
    </Text>
  );
};

export const SelectedStatus = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="400" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        {isEqual(selectedItemCount, totalItemCount)
          ? `${t`All `}`
          : selectedItemCount == 1
            ? `${t`${selectedItemCount} Status`}`
            : selectedItemCount > 1
              ? `${t`${selectedItemCount} Statuses`}`
              : `${t`Status`}`}
      </Text>
    </Box>
  );
};
export const Sync = () => {
  const colDefs = useState<ColDef[]>(attributeColDefs)[0];
  const [searchQuery, setSearchQuery] = useState('');
  const debounceSearchQuery = useDeferredValue(searchQuery?.trim());
  const [selectedErrorType, setSelectedErrorType] = useState<EventErrorTypes>(Error.ALL);
  const [selectedStatus, setSelectedStatus] = useState<EventStatusTypes[]>([
    EventNotificationStatus.ERROR,
    EventNotificationStatus.PUBLISH,
  ]);
  const [selectedNotificationType, setSelectedNotificationType] = useState<EventNotificationTypeId>(
    EventNotificationTypeId.ALL
  );
  const [selectedSortBy, setSelectedSortBy] = useState<SortType>({
    sortColumn: EventNotificationSortByTerm.CREATED_AT,
    sortOrder: SortOrder.ASC,
  });
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set([]));
  const [scheduleSync, setScheduleSync] = useState(scheduleList[0]);
  const [syncError, setSyncError] = useState<ModalError<SyncEventNotificationErrorCode>>(null);
  const [getEventNotifications, { data, error, loading, fetchMore, refetch }] = useLazyQuery<
    { eventNotifications: EventNotificationList },
    {
      input: EventNotificationInput;
      paging?: PagingInput;
    }
  >(GET_EVENT_NOTIFICATIONS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const client = useApolloClient();
  const eventList: EventNotification[] = get(data, 'eventNotifications.items', []);
  const eventTotalRecords = get(data, 'eventNotifications.total', 0);
  const pageLimit = 12;
  const eventTotalPages = eventTotalRecords ? Math.ceil(eventTotalRecords / pageLimit) : 0;
  const eventCursor = Number(get(data, 'eventNotifications.cursor', 1));

  const dispatchToast = useToastsDispatch();
  const onCompleted = (syncData) => {
    const error = get(syncData, 'syncEventNotifications.errors.0.error');
    if (error) {
      setSyncError(error);
    } else {
      client.refetchQueries({
        include: ['eventNotifications'],
      });
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: t`Sync is Succesfull.`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
    }
  };

  const onError = (error) => {
    setSyncError(error);
  };

  const [syncEventNotifications, { loading: syncMutationLoading }] = useMutation<
    { syncEventNotifications: SyncEventNotificationsPayload },
    { input: SyncEventNotificationsInput }
  >(SYNC_EVENT_NOTIFICATIONS, {
    onCompleted: onCompleted,
    onError: onError,
  });

  useEffect(() => {
    if (debounceSearchQuery.length > 0 && debounceSearchQuery.length < 3) return;
    const input = {};
    const eventNotificationsFilter: EventNotificationFilterByTermInput[] = [];
    if (selectedErrorType && selectedErrorType !== Error.ALL) {
      eventNotificationsFilter.push(getErrorFilter(selectedErrorType));
    }
    if (selectedStatus && selectedStatus.length !== statusList.length) {
      eventNotificationsFilter.push(getStatusFilter(selectedStatus));
    }
    if (selectedNotificationType && selectedNotificationType !== EventNotificationTypeId.ALL) {
      eventNotificationsFilter.push({
        operator: FilterOperator.EQUALS,
        term: EventNotificationFilterByTerm.NOTIFICATION_TYPE_ID,
        value: selectedNotificationType,
      });
    }
    if (debounceSearchQuery.length >= 3) {
      eventNotificationsFilter.push({
        operator: FilterOperator.CONTAINS,
        term: EventNotificationFilterByTerm.SEARCH_ERRORS,
        value: debounceSearchQuery,
      });
    }
    if (selectedEntities.size) {
      eventNotificationsFilter.push({
        operator: FilterOperator.IN,
        term: EventNotificationFilterByTerm.ENTITY_TYPE,
        values: Array.from(selectedEntities),
      });
    }
    if (selectedSortBy) {
      const sortByFilter = {
        term: selectedSortBy.sortColumn,
        sortOrder: selectedSortBy.sortOrder,
      };
      input['sortByFilter'] = sortByFilter;
    }
    input['complexFilter'] = {
      and: eventNotificationsFilter,
    };
    getEventNotifications({
      variables: {
        input,
        paging: {
          limit: pageLimit,
          cursor: '1',
        },
      },
    });
  }, [
    selectedErrorType,
    selectedStatus,
    selectedNotificationType,
    debounceSearchQuery,
    selectedEntities,
    selectedSortBy,
  ]);

  const onEntitySelected = (selectedEntities: Set<string>) => {
    setSelectedEntities(selectedEntities);
  };

  const handleScroll = () => {
    if (eventCursor <= eventTotalPages && !loading) {
      fetchMore({
        variables: {
          paging: {
            cursor: eventCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  };

  const onClick = useCallback(
    (data) => {
      syncEventNotifications({
        variables: {
          input: {
            complexFilter: {
              and: [
                {
                  operator: FilterOperator.EQUALS,
                  term: EventNotificationFilterByTerm.NOTIFICATION_ID,
                  value: data.id,
                },
              ],
            }
          },
        },
      });
    },
    [data]
  );

  const onSortChanged = useCallback((event: SortChangedEvent) => {
    const sortModel: SortModelItem[] = event.api.getColumnState()
      .filter(col => col.sort !== null && col.sort !== undefined)
      .map(col => ({
        colId: col.colId,
        sort: col.sort as 'asc' | 'desc',
      }));

    const mappedSort = mapAgGridSortToGraphQL(sortModel);

    if (mappedSort) {
      // Only update if the sort has actually changed to prevent infinite loops
      if (mappedSort.sortColumn !== selectedSortBy.sortColumn ||
        mappedSort.sortOrder !== selectedSortBy.sortOrder) {
        setSelectedSortBy(mappedSort);
      }
    } else {
      // If no valid sort or column not supported, use default sort
      const defaultSort = {
        sortColumn: EventNotificationSortByTerm.CREATED_AT,
        sortOrder: SortOrder.ASC,
      };
      if (selectedSortBy.sortColumn !== defaultSort.sortColumn ||
        selectedSortBy.sortOrder !== defaultSort.sortOrder) {
        setSelectedSortBy(defaultSort);
      }
    }
  }, [selectedSortBy]);

  const eventNotificationFilterInput: EventNotificationFilterInput = {
    and: [
      {
        term: EventNotificationFilterByTerm.ERROR_TYPE,
        value: selectedErrorType,
        operator: FilterOperator.EQUALS,
      },
      {
        term: EventNotificationFilterByTerm.STATUS,
        values: selectedStatus,
        operator: FilterOperator.IN,
      },
      {
        term: EventNotificationFilterByTerm.NOTIFICATION_TYPE_ID,
        value: selectedNotificationType,
        operator: FilterOperator.EQUALS,
      },
    ],
  };
  return (
    <ProjectAdminContainerStyled>
      <ProjectAdminHeaderStyled>
        <Text family="graphik" size="600" weight="600" color="white">
          <Trans>Sync</Trans>
        </Text>
      </ProjectAdminHeaderStyled>
      <SubContainerStyled style={{ paddingBottom: '16px' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <Box py={{ sm: '550' }} px={{ sm: '450' }} gap={{ sm: '700' }} css="width: 100%">
            <Box flexDirection={{ sm: 'column' }} gap={{ sm: '300' }}>
              <Text weight={'600'}>
                <Trans>Sync Data</Trans>
              </Text>
              <Text size={'300'} color="red.700">
                <Trans>This may take several hours to complete</Trans>
              </Text>
            </Box>
            <Box gap={{ sm: '400' }} css="width: 100%">
              <Box>
                <SearchFieldComponentStyled
                  size={TextFieldSize.LARGE}
                  iconSize={IconSize.MEDIUM}
                  placeholder={t`Search`}
                  type="search"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchQuery(e.target.value);
                  }}
                  value={searchQuery}
                />
              </Box>
              <FormControl
                style={{ flexGrow: 1 }}
                label={
                  <Text family="graphik" size="300" color="bechtel.blue.800">
                    <Trans>Error Type</Trans>
                  </Text>
                }
              >
                <Select
                  data-testid="error-type"
                  size={TextFieldSize.MEDIUM}
                  appearance={TextFieldAppearance.NORMAL}
                  items={errorList}
                  itemComponent={DropdownItem}
                  selectedComponent={DropdownItem}
                  initialSelectedItem={errorList[0]}
                  popperStyle={{ zIndex: 2 }}
                  onSelectedItemChange={(e) => {
                    setSelectedErrorType(e.selectedItem.value as EventErrorTypes);
                  }}
                />
              </FormControl>
              <FormControl
                style={{ flexGrow: 1 }}
                label={
                  <Text family="graphik" size="300" color="bechtel.blue.800">
                    <Trans>Status</Trans>
                  </Text>
                }
              >
                <MultiSelectCheckbox
                  key={selectedStatus.join(',')}
                  items={statusList}
                  itemComponent={DropdownItem}
                  selectedComponent={SelectedStatus}
                  initialSelectedItems={statusList.filter((item) =>
                    selectedStatus.includes(item.value as EventStatusTypes)
                  )}
                  size={TextFieldSize.MEDIUM}
                  showSelectAll={true}
                  confirmSelection={true}
                  appearance={TextFieldAppearance.NORMAL}
                  displayKeyField="label"
                  onSelectedItemsChange={async (selectedItems) => {
                    setSelectedStatus(selectedItems.map((item) => item.value as EventStatusTypes));
                  }}
                ></MultiSelectCheckbox>
              </FormControl>
              <FormControl
                style={{ flexGrow: 1 }}
                label={
                  <Text family="graphik" size="300" color="bechtel.blue.800">
                    <Trans>Notification Type</Trans>
                  </Text>
                }
              >
                <Select
                  data-testid="notificationType"
                  size={TextFieldSize.MEDIUM}
                  appearance={TextFieldAppearance.NORMAL}
                  items={notificationTypes}
                  itemComponent={DropdownItem}
                  selectedComponent={DropdownItem}
                  initialSelectedItem={notificationTypes.find((item) => {
                    return item.value === EventNotificationTypeId.ALL;
                  })}
                  popperStyle={{ zIndex: 2 }}
                  onSelectedItemChange={(e) => {
                    setSelectedNotificationType(e.selectedItem.value as EventNotificationTypeId);
                  }}
                />
              </FormControl>
            </Box>
          </Box>
          <Separator
            size="100%"
            type={SeparatorType.HORIZONTAL}
            spacing="000"
            style={{ backgroundColor: theme.colors.grey['300'] as string }}
          />
          <Box py={{ sm: '450' }} px={{ sm: '500' }} gap={{ sm: '400' }}>
            <EntityStats
              onEntitySelected={onEntitySelected}
              selectedEventNotificationFilter={eventNotificationFilterInput}
            />
          </Box>
        </Box>
        <Box
          mt={{ sm: '500' }}
          flexDirection={{ sm: 'column' }}
          data-testid="entity-list-container"
          flexGrow={{ sm: 1 }}
          flexShrink={{ sm: 0 }}
          flexBasis={{ sm: '100%' }}
          minWidth={{ sm: '100%' }}
          minHeight={{ sm: '100%' }}
          display="flex"
          overflow="hidden"
          css="overflow-y:auto; position: relative;"
        >
          {!error && eventList.length > 0 && (
            <ClientGrid
              loading={loading}
              colDefs={colDefs}
              rowData={eventList}
              rowHeight={30}
              headerHeight={30}
              floatingFilterHeight={30}
              onClick={onClick}
              fontSize={12}
              handleScroll={handleScroll}
              onSortChanged={onSortChanged}
              initialSort={mapGraphQLSortToAgGrid(selectedSortBy) || undefined}
            />
          )}

          {loading && (
            <Box data-testid="entity-list-loader" justifyContent={{ sm: 'center' }} css="width:100%" mt={{ sm: '400' }}>
              <Spinner size={SpinnerSize.MEDIUM} />
            </Box>
          )}
        </Box>
        {!loading && error && (
          <Box
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            flexDirection={{ sm: 'column' }}
            p={{ sm: '500' }}
            data-testid="user-list-retry"
          >
            <ErrorRetry refetch={refetch}>
              <Trans>There was a problem loading the events. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </Box>
        )}
        {!loading && !error && !eventList.length && (
          <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }} css="width:100%">
            <Heading size="400" weight="600" color="grey.600">
              <Trans>No events found.</Trans>
            </Heading>
            <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
              <Text size="400" family="graphik" color="grey.600" style={{ textAlign: 'center', lineHeight: '1.57' }}>
                <Trans>Try changing your search or filters to view events.</Trans>
              </Text>
            </Box>
            <FilterIcon style={{ width: '124px', height: '110px' }} />
          </Box>
        )}
      </SubContainerStyled>
      {syncError && <ErrorModal title={t`Sync Error`} error={getError(syncError)} close={() => setSyncError(null)} />}
    </ProjectAdminContainerStyled>
  );
};
